import { useCallback, useEffect, useState } from 'react'

import { DatePicker } from '../../lib/DatePicker'
import { monthRangeThai } from '../../lib/datetime'
import { errorText } from '../../lib/offsite'
import { useSession } from '../../lib/SessionContext'
import { SortBar, SortTh, sortRows, useSort } from '../../lib/Sort'
import { SectionHead } from '../offsite/parts'

/* ============================================================================
   สรุปการลงเวลาผ่าน LINE รายคน
   API: backend/src/routes/adminOffsiteSummary.js · lib/offsiteSummary.js

   ── ต่างจาก "ประวัติการลงเวลาผ่าน LINE" (OffsiteCheckins) ยังไง ──────────────
   หน้านั้นเป็น "รายรอย" ทีละการแตะ — อยู่ที่ไหน ห่างเท่าไร รูปอะไร
   หน้านี้เป็น "สรุปรายคน" ในช่วงที่เลือก — ใครลงกี่ครั้ง นอกรัศมีกี่ครั้ง ด้วยสิทธิ์อะไร
   ทั้งคู่อ่านตารางเดียวกัน (HR_TA_DATA_ONLINE) ต่างที่ GROUP BY รายคน

   ── ทำไมเน้น "นอกรัศมี" กับ "ช่วงเปิดชั่วคราว" ───────────────────────────────
   DDL ของตารางนี้ (005) ระบุชัดว่าคำถามที่ถามบ่อยคือ "ใครลงเวลา anywhere บ่อย
   ผิดปกติ" และ "เดือนนี้ใครลงด้วยช่วงเวลาฉุกเฉิน (window)" — window ควรเป็น
   ข้อยกเว้น ไม่ใช่ของประจำ หน้านี้จึงดันสองคอลัมน์นั้นให้เห็นก่อน
   ============================================================================ */

type Row = {
  empCode: string
  empName: string
  total: number
  inCount: number
  outCount: number
  offsiteCount: number
  windowCount: number
  permissionCount: number
  activeDays: number
  /** 'YYYY-MM-DD HH:mm:ss' เวลาไทย */
  firstAt: string | null
  lastAt: string | null
}

type SiteRow = {
  siteCode: string | null
  siteName: string | null
  total: number
  people: number
}

type Result = {
  range: { from: string; to: string }
  items: Row[]
  totals: { people: number; checkins: number; offsite: number; window: number }
  sites: SiteRow[]
}

const BASE = '/api/admin/offsite-summary'
const nf = new Intl.NumberFormat('th-TH')

export default function OffsiteSummary() {
  const { call } = useSession()

  /* ค่าเริ่มต้น = วันที่ 1 ถึงสิ้นเดือนของเดือนปัจจุบัน — แบบเดียวกับหน้าประวัติ
     (รอบตรวจเป็นรายเดือน การถามว่า "เดือนนี้ใครลงจากที่ไหนบ้าง" คือคำถามจริง) */
  const [range] = useState(() => monthRangeThai())
  const [from, setFrom] = useState(range.from)
  const [to, setTo] = useState(range.to)
  const [term, setTerm] = useState('')
  const [q, setQ] = useState('')
  const [offsiteOnly, setOffsiteOnly] = useState(false)

  const [data, setData] = useState<Result | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // ค่าเริ่มต้นเรียง "คนที่ลงนอกรัศมีมากสุดขึ้นก่อน" — ตรงกับ ORDER BY ฝั่ง backend
  const sort = useSort([{ key: 'offsite', dir: 'desc' }])

  const load = useCallback(async () => {
    if (!from || !to) return
    setLoading(true)
    try {
      const qs = new URLSearchParams({ from, to })
      if (q) qs.set('q', q)
      if (offsiteOnly) qs.set('offsiteOnly', 'true')
      setData(await call<Result>(`${BASE}?${qs}`))
      setError(null)
    } catch (err) {
      setError(errorText(err))
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [call, from, to, q, offsiteOnly])

  useEffect(() => {
    void load()
  }, [load])

  const rows = sortRows(data?.items ?? [], sort.sort, {
    empCode: (r) => r.empCode,
    total: (r) => r.total,
    offsite: (r) => r.offsiteCount,
    window: (r) => r.windowCount,
    activeDays: (r) => r.activeDays,
  })

  const exportCsv = () => {
    const head = [
      'รหัส', 'ชื่อ', 'ลงทั้งหมด', 'เข้า', 'ออก',
      'นอกรัศมี', 'ด้วยใบอนุญาต', 'ด้วยช่วงเปิดชั่วคราว', 'จำนวนวัน', 'ครั้งแรก', 'ครั้งล่าสุด',
    ]
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`
    const body = rows.map((r) =>
      [
        r.empCode, r.empName, r.total, r.inCount, r.outCount,
        r.offsiteCount, r.permissionCount, r.windowCount, r.activeDays, r.firstAt ?? '', r.lastAt ?? '',
      ].map(esc).join(','),
    )
    // BOM ให้ Excel อ่านไทยออก
    const blob = new Blob(['' + [head.map(esc).join(','), ...body].join('\r\n')], {
      type: 'text/csv;charset=utf-8',
    })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `สรุปลงเวลาผ่านLINE_${from}_ถึง_${to}.csv`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  return (
    <>
      <h1>สรุปการลงเวลานอกสถานที่</h1>
      <p className="meta">
        ใครลงเวลาผ่าน LINE บ่อยแค่ไหน จากที่ไหนบ้าง ในช่วงที่เลือก —
        เน้น <strong>จำนวนครั้งที่ลงนอกรัศมี</strong> และ
        ครั้งที่ผ่านด้วย<strong>ช่วงเปิดชั่วคราว</strong> ซึ่งควรเป็นข้อยกเว้น ไม่ใช่ของประจำ
      </p>

      {error && <p className="error">โหลดข้อมูลไม่สำเร็จ — {error}</p>}

      <section className="card">
        <SectionHead
          title={data ? `${data.range.from} ถึง ${data.range.to}` : 'สรุปการลงเวลานอกสถานที่'}
          meta={data ? `${nf.format(rows.length)} คน` : undefined}
          onReload={() => void load()}
          loading={loading}
        />

        <form
          className="toolbar"
          onSubmit={(e) => {
            e.preventDefault()
            setQ(term.trim())
          }}
        >
          <label>
            ตั้งแต่วันที่
            <DatePicker value={from} onChange={setFrom} />
          </label>
          <label>
            ถึงวันที่
            <DatePicker value={to} onChange={setTo} min={from} />
          </label>
          <label>
            ค้นหา
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="รหัสพนักงาน หรือชื่อ"
            />
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={offsiteOnly}
              onChange={(e) => setOffsiteOnly(e.target.checked)}
            />
            เฉพาะคนที่เคยลงนอกรัศมี
          </label>
          <button type="submit" disabled={loading}>
            ค้นหา
          </button>
          {rows.length > 0 && (
            <button type="button" className="ghost" onClick={exportCsv}>
              ดาวน์โหลด CSV
            </button>
          )}
        </form>

        {data && (
          <p className="meta">
            <strong>{nf.format(data.totals.people)}</strong> คน ·{' '}
            {nf.format(data.totals.checkins)} ครั้ง · นอกรัศมี{' '}
            <strong>{nf.format(data.totals.offsite)}</strong> ครั้ง · ด้วยช่วงเปิดชั่วคราว{' '}
            <strong>{nf.format(data.totals.window)}</strong> ครั้ง
          </p>
        )}

        {data === null ? (
          <p className="meta">กำลังโหลด…</p>
        ) : rows.length === 0 ? (
          <p className="meta">
            ไม่มีการลงเวลาผ่าน LINE ในช่วงที่เลือก
            {offsiteOnly && ' (ที่ลงนอกรัศมี)'}
          </p>
        ) : (
          <>
            <SortBar
              sort={sort.sort}
              labels={{
                offsite: 'นอกรัศมี',
                total: 'ลงทั้งหมด',
                window: 'ช่วงเปิดชั่วคราว',
                empCode: 'พนักงาน',
                activeDays: 'จำนวนวัน',
              }}
              onClear={() => sort.setSort([])}
            />

            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <SortTh label="พนักงาน" sortKey="empCode" sort={sort.sort} onToggle={sort.toggle} />
                    <SortTh label="ลงทั้งหมด" sortKey="total" sort={sort.sort} onToggle={sort.toggle} />
                    <th>เข้า / ออก</th>
                    <SortTh label="นอกรัศมี" sortKey="offsite" sort={sort.sort} onToggle={sort.toggle} />
                    <SortTh label="ช่วงเปิดชั่วคราว" sortKey="window" sort={sort.sort} onToggle={sort.toggle} />
                    <SortTh label="จำนวนวัน" sortKey="activeDays" sort={sort.sort} onToggle={sort.toggle} />
                    <th>ช่วงที่ลง</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.empCode}>
                      <td>
                        <strong>{r.empCode}</strong>
                        <div className="meta">{r.empName || '-'}</div>
                      </td>
                      <td>
                        <strong>{nf.format(r.total)}</strong>
                      </td>
                      <td className="meta">
                        {nf.format(r.inCount)} / {nf.format(r.outCount)}
                      </td>
                      <td>
                        {r.offsiteCount > 0 ? (
                          <span className="chip chip--warn">{nf.format(r.offsiteCount)}</span>
                        ) : (
                          <span className="meta">0</span>
                        )}
                      </td>
                      <td>
                        {r.windowCount > 0 ? (
                          <span className="chip chip--warn">{nf.format(r.windowCount)}</span>
                        ) : (
                          <span className="meta">0</span>
                        )}
                      </td>
                      <td className="meta">{nf.format(r.activeDays)}</td>
                      <td className="nowrap meta">
                        {r.firstAt ? r.firstAt.slice(0, 10) : '-'}
                        {r.lastAt && r.lastAt.slice(0, 10) !== r.firstAt?.slice(0, 10) && (
                          <> → {r.lastAt.slice(0, 10)}</>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      {data && data.sites.length > 0 && (
        <section className="card">
          <h2>ลงจากที่ไหนบ้าง</h2>
          <p className="meta">จำนวนครั้งรวมในช่วงที่เลือก แยกตามสถานที่</p>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>สถานที่</th>
                  <th>จำนวนครั้ง</th>
                  <th>จำนวนคน</th>
                </tr>
              </thead>
              <tbody>
                {data.sites.map((s) => (
                  <tr key={s.siteCode ?? '__offsite__'}>
                    <td>
                      {s.siteName ? (
                        <>
                          {s.siteName}
                          {s.siteCode && <span className="meta"> ({s.siteCode})</span>}
                        </>
                      ) : (
                        <span className="chip chip--warn">นอกสถานที่</span>
                      )}
                    </td>
                    <td>
                      <strong>{nf.format(s.total)}</strong>
                    </td>
                    <td className="meta">{nf.format(s.people)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  )
}
